import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEye,
  faEdit,
  faTrash,
  faList,
  faUsers,
  faBuilding,
  faTag,
  faLayerGroup,
  faSchool,
  faMagic,
  faPaperPlane,
  faCalendarAlt,
  faGraduationCap,
  faFilter,
  faSearch,
  faRefresh,
  faInfoCircle,
  faMars,
  faVenus,
} from "@fortawesome/free-solid-svg-icons";
import "./dashboard.css";
import "./stdSections.css";
import Sidebar from "./Sidebar";

// Base URL for PHP backend
const BASE_URL = "https://mhsdev.online/MHS-WEB/api";
const SECTIONS_API = `${BASE_URL}/StdSectionsCrud.php`;

const ManageSections = () => {
  // State for classes and sections
  const [classesData, setClassesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // State for modals
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [selectedSection, setSelectedSection] = useState(null);
  const [addingSection, setAddingSection] = useState(false);

  // State for new section form
  const [newSection, setNewSection] = useState({
    className: "",
    department: "",
    sectionFor: "",
    sectionName: "",
    sectionHint: "",
  });

  // Class list for dropdown
  const classList = [
    "Play Group",
    "Nursery",
    "Prep",
    "Class 1",
    "Class 2",
    "Class 3",
    "Class 4",
    "Class 5",
    "Class 6",
    "Class 7",
    "Class 8",
    "Class Pre 9",
    "Class 9",
    "Class 10",
  ];

  // Department options for special classes
  const departmentOptions = ["Computer Science", "Bio Science", "Arts"];

  // Fetch all sections grouped by class
  const fetchAllSections = async () => {
    setLoading(true);
    try {
      const response = await axios.get(SECTIONS_API, {
        params: { action: "fetch-sections" },
        headers: { "Content-Type": "application/json" },
      });

      if (response.data && response.data.success) {
        const sections = response.data.data || [];

        // Group sections by class
        const groupedByClass = {};

        sections.forEach((section) => {
          const className = section.class_name;
          if (!groupedByClass[className]) {
            groupedByClass[className] = {
              className: className,
              sections: [],
              stats: {
                total: 0,
                girls: 0,
                boys: 0,
                departments: new Set(),
              },
            };
          }

          groupedByClass[className].sections.push(section);
          groupedByClass[className].stats.total++;

          if (section.section_for === "Girls") {
            groupedByClass[className].stats.girls++;
          } else if (section.section_for === "Boys") {
            groupedByClass[className].stats.boys++;
          }

          if (section.department) {
            groupedByClass[className].stats.departments.add(section.department);
          }
        });

        // Convert to array and sort classes
        const classesArray = Object.values(groupedByClass).sort((a, b) => {
          const order = classList;
          return order.indexOf(a.className) - order.indexOf(b.className);
        });

        setClassesData(classesArray);
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.data?.error || "Failed to fetch sections",
          confirmButtonColor: "#2E2A74",
        });
      }
    } catch (error) {
      console.error("Error fetching sections:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.error ||
          "Failed to fetch sections. Please try again.",
        confirmButtonColor: "#2E2A74",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch sections when component mounts
  useEffect(() => {
    fetchAllSections();
  }, []);

  // Handle class selection for adding new section
  const handleSelectClassForAdd = (className) => {
    setSelectedClass(className);
    setNewSection({
      className: className,
      department: "",
      sectionFor: "",
      sectionName: "",
      sectionHint: "",
    });
    setShowAddModal(true);
  };

  // Handle section details view
  const handleViewDetails = (section) => {
    setSelectedSection(section);
    setShowDetailsModal(true);
  };

  // Handle edit section
  const handleEditSection = (section) => {
    setSelectedSection(section);
    setNewSection({
      className: section.class_name,
      department: section.department || "",
      sectionFor: section.section_for,
      sectionName: section.section_name,
      sectionHint: section.section_hint,
    });
    setShowAddModal(true);
  };

  // Handle delete section
  const handleDeleteSection = (sectionId, sectionName, className) => {
    Swal.fire({
      title: "Delete Section",
      text: `Are you sure you want to delete "${sectionName}" from ${className}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#2E2A74",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
      reverseButtons: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await axios.post(
            SECTIONS_API,
            {
              action: "delete-section",
              id: sectionId,
            },
            {
              headers: { "Content-Type": "application/json" },
            }
          );

          if (response.data.success) {
            Swal.fire({
              icon: "success",
              title: "Deleted!",
              text: "Section has been deleted successfully.",
              confirmButtonColor: "#2E8B57",
              timer: 3000,
            });
            fetchAllSections();
          } else {
            throw new Error(response.data.error || "Failed to delete section");
          }
        } catch (error) {
          Swal.fire({
            icon: "error",
            title: "Error",
            text:
              error.response?.data?.error ||
              "Failed to delete section. Please try again.",
            confirmButtonColor: "#2E2A74",
          });
        }
      }
    });
  };

  // Handle new section form changes
  const handleNewSectionChange = (field, value) => {
    const updatedSection = { ...newSection, [field]: value };

    // Auto-generate section hint
    if (
      field === "sectionName" ||
      field === "sectionFor" ||
      field === "department"
    ) {
      const department = updatedSection.department || "";
      const sectionFor = updatedSection.sectionFor || "";
      const sectionName = updatedSection.sectionName || "";

      if (isSpecialClass(updatedSection.className)) {
        updatedSection.sectionHint = department
          ? `${sectionName} - ${department} - ${sectionFor}`
          : `${sectionName} - ${sectionFor}`;
      } else {
        updatedSection.sectionHint = `${sectionName} - ${sectionFor}`;
      }
    }

    setNewSection(updatedSection);
  };

  // Check if class is special (requires department)
  const isSpecialClass = (className) => {
    return ["Class Pre 9", "Class 9", "Class 10"].includes(className);
  };

  // Submit new section
  const handleSubmitSection = async () => {
    // Validation
    if (!newSection.sectionName.trim()) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Please enter a section name",
        confirmButtonColor: "#2E2A74",
      });
      return;
    }

    if (!newSection.sectionFor) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Please select section for Girls or Boys",
        confirmButtonColor: "#2E2A74",
      });
      return;
    }

    if (isSpecialClass(newSection.className) && !newSection.department) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Department is required for this class",
        confirmButtonColor: "#2E2A74",
      });
      return;
    }

    setAddingSection(true);

    try {
      const sectionData = {
        className: newSection.className,
        sections: [
          {
            department: newSection.department || "",
            sectionFor: newSection.sectionFor,
            sectionHint: newSection.sectionHint,
            sectionName: newSection.sectionName,
          },
        ],
      };

      const response = await axios.post(
        SECTIONS_API,
        {
          action: "add-sections",
          ...sectionData,
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      if (response.data.success) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: response.data.message || "Section added successfully!",
          confirmButtonColor: "#2E8B57",
          timer: 3000,
        });

        setShowAddModal(false);
        setNewSection({
          className: "",
          department: "",
          sectionFor: "",
          sectionName: "",
          sectionHint: "",
        });
        fetchAllSections();
      } else {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.data.error || "Failed to add section",
          confirmButtonColor: "#2E2A74",
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text:
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to add section. Please try again.",
        confirmButtonColor: "#2E2A74",
      });
    } finally {
      setAddingSection(false);
    }
  };

  // Filter classes based on search term
  const filteredClasses = classesData.filter(
    (cls) =>
      cls.className.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cls.sections.some(
        (section) =>
          section.section_name
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          section.section_hint
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (section.department &&
            section.department.toLowerCase().includes(searchTerm.toLowerCase()))
      )
  );

  return (
    <div className="dashboard-wrapper">
      <Sidebar />

      <div className="main-content">
        <section className="content">
          <div className="content-header">
            <h1>Manage Sections</h1>
            <p className="subtitle">
              View and manage all class sections across your institution
            </p>
          </div>

          {/* Search and Filter Bar */}
          <div className="selection-card">
            <div className="card-header">
              <div className="header-icon-wrapper">
                <FontAwesomeIcon
                  icon={faFilter}
                  className="card-icon"
                  style={{ color: "white" }}
                />
              </div>
              <div>
                <h3>Filter Sections</h3>
                <p className="card-subtitle">
                  Search for specific classes or sections
                </p>
              </div>
            </div>

            <div className="selection-form">
              <div className="search-box">
                <div className="search-icon">
                  <FontAwesomeIcon icon={faSearch} />
                </div>
                <input
                  type="text"
                  placeholder="Search classes, sections, or departments..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                  style={{ paddingLeft: "5rem" }}
                />
                {searchTerm && (
                  <button
                    className="clear-search"
                    onClick={() => setSearchTerm("")}
                  >
                    Clear
                  </button>
                )}
              </div>

              <div className="table-actions">
                <button
                  className="refresh-btn"
                  onClick={fetchAllSections}
                  disabled={loading}
                  style={{ width: "10rem" }}
                >
                  <FontAwesomeIcon icon={faRefresh} spin={loading} />
                  <span>{loading ? "Refreshing..." : "Refresh Data"}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Classes Grid */}
          <div className="manage-sections-container">
            {loading ? (
              <div className="loading-state">
                <div className="loading-spinner"></div>
                <p>Loading sections...</p>
              </div>
            ) : filteredClasses.length > 0 ? (
              <div className="classes-grid">
                {filteredClasses.map((cls) => (
                  <div key={cls.className} className="class-card">
                    <div className="class-header">
                      <div className="class-title">
                        <div className="class-icon">
                          <FontAwesomeIcon icon={faSchool} />
                        </div>
                        <h3>{cls.className}</h3>
                      </div>

                      <div className="class-meta">
                        <div className="class-meta-item">
                          <FontAwesomeIcon icon={faLayerGroup} />
                          <span>{cls.stats.total} Sections</span>
                        </div>
                        <div className="class-meta-item">
                          <FontAwesomeIcon icon={faVenus} />
                          <span>{cls.stats.girls} Girls</span>
                        </div>
                        <div className="class-meta-item">
                          <FontAwesomeIcon icon={faMars} />
                          <span>{cls.stats.boys} Boys</span>
                        </div>
                      </div>
                    </div>

                    <div className="sections-list">
                      {cls.sections.length > 0 ? (
                        cls.sections.map((section) => (
                          <div key={section.id} className="section-item">
                            <div className="section-info">
                              <div className="section-name">
                                <FontAwesomeIcon
                                  icon={faTag}
                                  className="section-icon"
                                />
                                <span>{section.section_name}</span>
                              </div>
                              <div className="section-details">
                                <span
                                  className={`section-tag ${section.section_for.toLowerCase()}`}
                                >
                                  <FontAwesomeIcon
                                    icon={
                                      section.section_for === "Girls"
                                        ? faVenus
                                        : faMars
                                    }
                                  />
                                  {section.section_for}
                                </span>
                                {section.department && (
                                  <span className="section-tag department">
                                    <FontAwesomeIcon icon={faGraduationCap} />
                                    {section.department}
                                  </span>
                                )}
                                <span className="section-hint">
                                  {section.section_hint}
                                </span>
                              </div>
                            </div>
                            <div className="section-actions">
                              <button
                                className="section-action-btn details"
                                onClick={() => handleViewDetails(section)}
                                title="View Details"
                              >
                                <FontAwesomeIcon icon={faEye} />
                              </button>
                              <button
                                className="section-action-btn edit"
                                onClick={() => handleEditSection(section)}
                                title="Edit Section"
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                              <button
                                className="section-action-btn delete"
                                onClick={() =>
                                  handleDeleteSection(
                                    section.id,
                                    section.section_name,
                                    cls.className
                                  )
                                }
                                title="Delete Section"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="empty-sections">
                          <div className="empty-icon">
                            <FontAwesomeIcon icon={faLayerGroup} />
                          </div>
                          <h4>No Sections Yet</h4>
                          <p>This class doesn't have any sections</p>
                        </div>
                      )}
                    </div>

                    <div className="class-actions">
                      <div className="class-stats">
                        <div className="class-stat">
                          <FontAwesomeIcon icon={faGraduationCap} />
                          <span>
                            {cls.stats.departments.size > 0
                              ? `${cls.stats.departments.size} Dept(s)`
                              : "No Department"}
                          </span>
                        </div>
                      </div>
                      <button
                        className="add-section-btn"
                        onClick={() => handleSelectClassForAdd(cls.className)}
                      >
                        <FontAwesomeIcon icon={faPlus} />
                        <span>Add New Section</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : searchTerm ? (
              <div className="empty-state">
                <div className="empty-icon">
                  <FontAwesomeIcon icon={faSearch} />
                </div>
                <h4>No matching classes found</h4>
                <p>Try adjusting your search terms</p>
              </div>
            ) : (
              <div className="empty-state">
                <div className="empty-icon">
                  <FontAwesomeIcon icon={faSchool} />
                </div>
                <h4>No classes found</h4>
                <p>No sections have been created yet</p>
                <button
                  className="add-section-btn"
                  onClick={() => {
                    if (classList.length > 0) {
                      handleSelectClassForAdd(classList[0]);
                    }
                  }}
                  style={{ marginTop: "20px" }}
                >
                  <FontAwesomeIcon icon={faPlus} />
                  <span>Create Your First Section</span>
                </button>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Section Details Modal */}
      {showDetailsModal && selectedSection && (
        <div className="modal-overlay">
          <div className="section-details-modal">
            <div className="details-modal-header">
              <h3>Section Details</h3>
              <p className="modal-subtitle">
                Detailed information about this section
              </p>
            </div>

            <div className="details-modal-body">
              <div className="detail-row">
                <span className="detail-label">Class Name</span>
                <div className="detail-value">
                  <FontAwesomeIcon icon={faSchool} className="detail-icon" />
                  <div className="value-text">{selectedSection.class_name}</div>
                </div>
              </div>

              <div className="detail-row">
                <span className="detail-label">Section Name</span>
                <div className="detail-value">
                  <FontAwesomeIcon icon={faTag} className="detail-icon" />
                  <div className="value-text">
                    {selectedSection.section_name}
                  </div>
                </div>
              </div>

              <div className="detail-row">
                <span className="detail-label">Section For</span>
                <div className="detail-value">
                  <FontAwesomeIcon
                    icon={
                      selectedSection.section_for === "Girls" ? faVenus : faMars
                    }
                    className="detail-icon"
                  />
                  <div className="value-text">
                    {selectedSection.section_for}
                  </div>
                </div>
              </div>

              {selectedSection.department && (
                <div className="detail-row">
                  <span className="detail-label">Department</span>
                  <div className="detail-value">
                    <FontAwesomeIcon
                      icon={faGraduationCap}
                      className="detail-icon"
                    />
                    <div className="value-text">
                      {selectedSection.department}
                    </div>
                  </div>
                </div>
              )}

              <div className="detail-row">
                <span className="detail-label">Section Hint</span>
                <div className="detail-value">
                  <FontAwesomeIcon icon={faMagic} className="detail-icon" />
                  <div className="value-text">
                    {selectedSection.section_hint}
                  </div>
                </div>
              </div>

              <div className="detail-row">
                <span className="detail-label">Created On</span>
                <div className="detail-value">
                  <FontAwesomeIcon
                    icon={faCalendarAlt}
                    className="detail-icon"
                  />
                  <div className="value-text">
                    {new Date(selectedSection.created_at).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      }
                    )}
                  </div>
                </div>
              </div>

              <div className="detail-row">
                <span className="detail-label">Last Updated</span>
                <div className="detail-value">
                  <FontAwesomeIcon
                    icon={faCalendarAlt}
                    className="detail-icon"
                  />
                  <div className="value-text">
                    {new Date(selectedSection.updated_at).toLocaleDateString(
                      "en-US",
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      }
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => setShowDetailsModal(false)}
              >
                <span>Close</span>
              </button>
              <button
                className="btn-submit"
                onClick={() => {
                  setShowDetailsModal(false);
                  handleEditSection(selectedSection);
                }}
              >
                <FontAwesomeIcon icon={faEdit} />
                <span>Edit Section</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Section Modal */}
      {showAddModal && selectedClass && (
        <div className="modal-overlay">
          <div className="add-section-modal">
            <div className="add-modal-header">
              <h3>{selectedSection ? "Edit Section" : "Add New Section"}</h3>
              <p className="modal-subtitle">
                {selectedSection
                  ? "Update the details for this section"
                  : `Add a new section to ${selectedClass}`}
              </p>
            </div>

            <div className="add-modal-body">
              {addingSection && (
                <div className="loading-overlay">
                  <div className="loading-spinner"></div>
                </div>
              )}

              <div className="add-section-form">
                <div className="modal-form-group">
                  <label>
                    Class Name <span className="required">*</span>
                  </label>
                  <input
                    type="text"
                    value={selectedClass}
                    className="modal-input readonly"
                    readOnly
                  />
                </div>

                {isSpecialClass(selectedClass) && (
                  <div className="modal-form-group">
                    <label>
                      Department <span className="required">*</span>
                    </label>
                    <select
                      value={newSection.department}
                      onChange={(e) =>
                        handleNewSectionChange("department", e.target.value)
                      }
                      className="modal-select"
                    >
                      <option value="" disabled>
                        Select Department
                      </option>
                      {departmentOptions.map((dept, idx) => (
                        <option key={idx} value={dept}>
                          {dept}
                        </option>
                      ))}
                    </select>
                    <p className="form-hint">
                      <FontAwesomeIcon icon={faInfoCircle} />
                      Department selection is required for this class
                    </p>
                  </div>
                )}

                <div className="form-row">
                  <div className="modal-form-group">
                    <label>
                      Section For <span className="required">*</span>
                    </label>
                    <select
                      value={newSection.sectionFor}
                      onChange={(e) =>
                        handleNewSectionChange("sectionFor", e.target.value)
                      }
                      className="modal-select"
                    >
                      <option value="" disabled>
                        Select Gender
                      </option>
                      <option value="Girls">Girls</option>
                      <option value="Boys">Boys</option>
                    </select>
                  </div>

                  <div className="modal-form-group">
                    <label>
                      Section Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      value={newSection.sectionName}
                      onChange={(e) =>
                        handleNewSectionChange("sectionName", e.target.value)
                      }
                      placeholder="e.g., A, B, Morning, Evening"
                      className="modal-input"
                    />
                  </div>
                </div>

                <div className="modal-form-group">
                  <label>Section Hint (Auto-generated)</label>
                  <div className="hint-display">
                    <input
                      type="text"
                      value={
                        newSection.sectionHint ||
                        "Fill in fields above to generate hint"
                      }
                      className="modal-input readonly"
                      readOnly
                    />
                    <span className="hint-icon">
                      <FontAwesomeIcon icon={faMagic} />
                    </span>
                  </div>
                  <p className="form-hint">
                    <FontAwesomeIcon icon={faInfoCircle} />
                    This field updates automatically based on your selections
                  </p>
                </div>
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="btn-cancel"
                onClick={() => {
                  setShowAddModal(false);
                  setSelectedSection(null);
                  setNewSection({
                    className: "",
                    department: "",
                    sectionFor: "",
                    sectionName: "",
                    sectionHint: "",
                  });
                }}
                disabled={addingSection}
              >
                <span>Cancel</span>
              </button>
              <button
                className="btn-submit"
                onClick={handleSubmitSection}
                disabled={addingSection}
              >
                <FontAwesomeIcon icon={faPaperPlane} />
                <span>
                  {addingSection
                    ? "Processing..."
                    : selectedSection
                    ? "Update Section"
                    : "Add Section"}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageSections;
